import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  AlertTriangle,
  ArrowUpRight,
  Receipt,
  ShoppingCart,
  CalendarCheck,
  ClipboardList,
  KeyRound,
  Mail,
  MailQuestionMark,
  MailWarning,
  RefreshCcw,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { getAdminDashboardStats } from "@/lib/admin-dashboard.functions";
import type { AdminWorkItemKey } from "@/lib/admin-dashboard";
import { visibleWorkItems } from "@/lib/admin-workload";
import { describeFailureSplit } from "@/lib/email-failures";
import { warmAdminQuery } from "@/lib/admin-loader";

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_admin/admin/")({
  // Arbeitsvorrat startet schon beim Überfahren des Menüpunkts.
  loader: ({ context }) => {
    warmAdminQuery(context.queryClient, {
      queryKey: ["admin", "dashboard-stats"],
      queryFn: () => getAdminDashboardStats(),
      staleTime: 60_000,
    });
  },
  component: AdminHome,
});

const ICONS: Record<AdminWorkItemKey, LucideIcon> = {
  // Ticket T4-Infra: Stille Störungen.
  silentFailures: AlertTriangle,
  inquiries: Mail,
  questionnaires: ClipboardList,
  credentials: KeyRound,
  electionOutcome: CalendarCheck,
  mocoSync: RefreshCcw,
  moduleInterest: Sparkles,
  ttpaReview: ShieldCheck,
  emailFailures: MailWarning,
  emailStuck: MailQuestionMark,
  purchaseFulfillment: ShoppingCart,
  purchaseInvoice: Receipt,
  purchaseDecision: AlertTriangle,
};

function AdminHome() {
  const statsFn = useServerFn(getAdminDashboardStats);
  // Genau eine Serverabfrage für die gesamte Startseite (Ticket AD4a).
  const statsQ = useQuery({
    queryKey: ["admin", "dashboard-stats"],
    queryFn: () => statsFn(),
    staleTime: 60_000,
  });

  const items = visibleWorkItems(statsQ.data?.items);
  const emptyWorkload = statsQ.isSuccess && items.length === 0;

  return (
    <div className="max-w-7xl">
      <AdminPageHeader
        title="Dashboard"
        description="Ihr Einstieg in den internen Bereich: hier stehen nur offene Vorgänge."
      />

      <h2 className="mt-10 font-display text-lg font-extrabold text-brand-petrol">
        Ihr Arbeitsvorrat
      </h2>

      {statsQ.isPending ? (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-lg border border-border bg-background p-6">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="mt-4 h-8 w-14" />
              <Skeleton className="mt-3 h-3 w-48" />
            </div>
          ))}
        </div>
      ) : null}

      {/* Ticket AV5: Der leere Vorrat ist künftig der Regelfall und bekommt
          deshalb eine ruhige Fläche statt einer knappen Zeile. */}
      {emptyWorkload ? (
        <div className="mt-4 rounded-lg border border-dashed border-border bg-secondary/30 p-10 text-center">
          <p className="font-display text-base font-extrabold text-brand-petrol">
            Nichts offen. Alle Vorgänge sind abgearbeitet.
          </p>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            Sobald ein Vorgang eintrifft, erscheint er hier. Alle Bereiche erreichen Sie
            jederzeit über die Seitenleiste.
          </p>
        </div>
      ) : null}


      {items.length > 0 ? (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map(({ item, count }) => {
            const Icon = ICONS[item.key];
            const warn = item.key === "mocoSync" && statsQ.data?.mocoSyncFailed;
            // Ticket W8: „3 an externe Empfänger, 9 intern" – fremde Empfänger zuerst.
            const splitLabel =
              item.key === "emailFailures"
                ? describeFailureSplit(
                    statsQ.data?.items.find((i) => i.key === "emailFailures")?.failureSplit,
                  )
                : null;
            return (
              <Link
                key={item.key}
                to={item.to}
                search={item.search as never}
                className="group rounded-lg border border-border bg-background p-6 transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 shrink-0 text-brand-petrol" aria-hidden="true" />
                  <p className="min-w-0 font-display text-sm font-extrabold text-brand-petrol">
                    {item.label}
                  </p>
                </div>
                <p className="mt-3 font-display text-3xl font-extrabold text-foreground">{count}</p>
                {item.key === "emailFailures" && splitLabel ? (
                  <p className="mt-2 text-xs font-semibold text-brand-orange">{splitLabel}</p>
                ) : null}
                <p className="mt-2 text-xs text-muted-foreground">{item.hint}</p>
                {warn ? (
                  <p className="mt-2 text-xs font-semibold text-brand-orange">
                    Der letzte Abgleich endete mit einem Fehler.
                  </p>
                ) : null}
                <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary group-hover:underline">
                  {item.action}
                  <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                </p>
              </Link>
            );
          })}
        </div>
      ) : null}

      {statsQ.isError ? (
        <p className="mt-3 text-xs text-muted-foreground" role="status">
          Der Arbeitsvorrat konnte nicht geladen werden. Die Bereiche sind über die
          Seitenleiste weiterhin erreichbar.
        </p>
      ) : null}

      {/* Ticket AV5: Kein Kachelraster „Inhalte pflegen" mehr – die Navigation
          gehört in die Seitenleiste, nicht zweimal auf die Startseite. */}
    </div>
  );
}
